/**
 * Site texts CRUD service — dual backend (Supabase | php-api).
 */
import { supabase } from "@/integrations/supabase/client";
import { isPhpApi } from "@/lib/backend-mode";
import { api, ApiError } from "@/lib/api";

export interface SiteTextRow {
  key: string;
  value: string;
  updated_at: string;
}

function wrap(what: string, e: unknown): never {
  if (e instanceof ApiError && e.status === 501) {
    throw new Error(`Backend PHP chưa hỗ trợ "${what}" (501). Cấu hình lại VITE_BACKEND_MODE=supabase nếu cần.`);
  }
  throw e;
}

export async function listSiteTexts(): Promise<SiteTextRow[]> {
  if (isPhpApi()) {
    try { return await api.get<SiteTextRow[]>("/site-texts"); }
    catch (e) { wrap("listSiteTexts", e); }
  }
  const { data, error } = await supabase.from("site_texts").select("*").order("key");
  if (error) throw error;
  return (data ?? []) as SiteTextRow[];
}

export async function listSiteTextsAsMap(): Promise<Record<string, string>> {
  if (isPhpApi()) {
    try {
      const rows = await api.get<{ key: string; value: string }[]>("/site-texts");
      const m: Record<string, string> = {};
      for (const r of rows ?? []) m[r.key] = r.value;
      return m;
    } catch (e) { wrap("listSiteTextsAsMap", e); }
  }
  const { data, error } = await supabase.from("site_texts").select("key,value");
  if (error) throw error;
  const m: Record<string, string> = {};
  for (const r of (data ?? []) as { key: string; value: string }[]) m[r.key] = r.value;
  return m;
}

export async function updateSiteText(key: string, value: string): Promise<void> {
  if (isPhpApi()) {
    try { await api.put(`/site-texts/${encodeURIComponent(key)}`, { value }); return; }
    catch (e) { wrap("updateSiteText", e); }
  }
  const { error } = await supabase.from("site_texts").update({ value }).eq("key", key);
  if (error) throw error;
}

export async function insertSiteText(key: string, value: string): Promise<void> {
  if (isPhpApi()) {
    try { await api.post(`/site-texts`, { key, value }); return; }
    catch (e) { wrap("insertSiteText", e); }
  }
  const { error } = await supabase.from("site_texts").insert({ key, value });
  if (error) throw error;
}

export async function upsertSiteText(key: string, value: string): Promise<void> {
  if (isPhpApi()) {
    try { await api.put(`/site-texts/${encodeURIComponent(key)}`, { value, upsert: true }); return; }
    catch (e) { wrap("upsertSiteText", e); }
  }
  const { error } = await supabase.from("site_texts").upsert({ key, value }, { onConflict: "key" });
  if (error) throw error;
}

export async function deleteSiteText(key: string): Promise<void> {
  if (isPhpApi()) {
    try { await api.del(`/site-texts/${encodeURIComponent(key)}`); return; }
    catch (e) { wrap("deleteSiteText", e); }
  }
  const { error } = await supabase.from("site_texts").delete().eq("key", key);
  if (error) throw error;
}
